import React, { useState } from 'react';
import { StyleSheet } from 'react-native';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { GameErrorOverlay } from '@/components/GameErrorOverlay';
import { Loading } from '@/components/Loading';
import { Screen } from '@/components/ScreenComponent';
import WebView from '@/components/Webview';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { RootStackParamList } from '@/navigation/types';
import { logger } from '@/utils/logging';

import { useRipsSessionUrl } from '../hooks/use-rips-session';
import { useRipsWebViewBridge } from '../hooks/use-rips-webview-bridge';

type ScreenProps = NativeStackScreenProps<RootStackParamList, 'RipsGameWebView'>;

const LOG_TAG = '[RipsGameWebView]';

export const RipsGameWebViewScreen = ({ route: { params } }: ScreenProps) => {
    const url = useRipsSessionUrl(params);

    const [webViewLoading, setWebViewLoading] = useState(true);

    const {
        injectedJavaScriptBeforeContentLoaded,
        onMessage,
        onShouldStartLoadWithRequest,
        onWebviewProcessTerminated,
        webViewRef,
        webViewKey,
        showErrorOverlay,
        retryWebView,
        navigateToLobby,
    } = useRipsWebViewBridge(url);

    return (
        <Screen unsafe>
            {url ? (
                <WebView
                    key={webViewKey}
                    ref={webViewRef}
                    source={{ uri: url }}
                    style={styles.webView}
                    mediaPlaybackRequiresUserAction={false}
                    allowsInlineMediaPlayback={true}
                    // On Android, onLoadStart also fires when the web app only changes its URL, without
                    // loading a new page. No onLoadEnd follows, so use `loading` instead of always
                    // setting true, or the spinner would never hide.
                    onLoadStart={({ nativeEvent }) => setWebViewLoading(nativeEvent.loading)}
                    onLoadEnd={() => setWebViewLoading(false)}
                    onError={({ nativeEvent }) =>
                        logger.error(LOG_TAG, 'Failed to load the Rips web app', {
                            code: nativeEvent.code,
                            description: nativeEvent.description,
                        })
                    }
                    onHttpError={({ nativeEvent }) =>
                        logger.error(LOG_TAG, 'Rips web app HTTP error', { statusCode: nativeEvent.statusCode })
                    }
                    injectedJavaScriptBeforeContentLoaded={injectedJavaScriptBeforeContentLoaded}
                    onMessage={onMessage}
                    onShouldStartLoadWithRequest={onShouldStartLoadWithRequest}
                    onContentProcessDidTerminate={onWebviewProcessTerminated}
                    onRenderProcessGone={onWebviewProcessTerminated}
                />
            ) : null}
            {webViewLoading ? (
                <Box style={[styles.loadingOverlay, { backgroundColor: ds.colorValue.background.neutral.base }]}>
                    <Loading />
                </Box>
            ) : null}
            {showErrorOverlay ? <GameErrorOverlay onReload={retryWebView} onGoToLobby={navigateToLobby} /> : null}
        </Screen>
    );
};

const styles = StyleSheet.create({
    webView: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    loadingOverlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
